'use strict';

// Bloco (não função) só pra isolar o escopo de `const` — evita "already declared" se o script acabar
// incluído duas vezes na mesma página (já aconteceu: layout-preference.v1.js era carregado 2x).
{
const DockLayoutPreference = {
    embedSessionKey: 'embed',

    // As poucas rotas -legacy cuja versão nova não mora no mesmo caminho sem o sufixo: tirá-lo aqui
    // daria 404. Continuam no visual antigo até ganharem equivalente, e aí saem desta lista.
    legacyPathsWithoutNewVersion: [
        'manage/category-legacy',     // wfform_category; manage/categories é outra coisa (categoria de processo)
        'manage/files-legacy',        // a versão nova mora em manage/processes/file/{id}
        'tracker-form-detail-legacy'  // a versão nova mora em workflow/tracker-form-detail
    ],

    init() {
        this.syncEmbedStateWithCurrentUrl();
    },

    hasEmbedInUrl() {
        return globalThis.location?.search.includes('embed=1');
    },

    clearEmbedSessionFlag() {
        try {
            globalThis.sessionStorage?.removeItem(this.embedSessionKey);
        } catch {
            // noop
        }
    },

    syncEmbedStateWithCurrentUrl() {
        if (!this.hasEmbedInUrl()) {
            this.clearEmbedSessionFlag();
        }
    },

    antiForgeryToken() {
        return document.querySelector('input[name=__RequestVerificationToken]')?.value ?? '';
    },

    // A decisão mora só em wUSER_PREFERENCE.StUseNewLayout — é o servidor que lê a preferência a cada
    // requisição pra escolher o layout (ver DockLayoutHelper), então aqui basta gravar e recarregar.
    async persist(endpoint) {
        if (!endpoint) return false;

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SML-AntiForgeryToken': this.antiForgeryToken()
                }
            });
            return response.ok;
        } catch {
            return false;
        }
    },

    // Devolve false quando a gravação falhou — quem chamou avisa a pessoa em vez de trocar o visual,
    // que voltaria atrás no login seguinte.
    async persistAndReload(endpoint, targetUrl) {
        if (!await this.persist(endpoint)) return false;

        const fallbackTargetUrl = targetUrl || globalThis.location?.href || globalThis.location?.pathname || '/';
        globalThis.location?.assign(fallbackTargetUrl);
        return true;
    },

    nativeEquivalentLegacyPaths: ['/report/main-legacy', '/report/my-legacy'],

    stripLegacySuffixIfKnown(url) {
        try {
            const parsed = new URL(url, globalThis.location?.origin);
            if (!this.nativeEquivalentLegacyPaths.includes(parsed.pathname)) return url;
            parsed.pathname = parsed.pathname.replace(/-legacy$/, '');
            return parsed.href;
        } catch {
            return url;
        }
    },

    setAndReload(value, targetUrl) {
        const normalizedValue = this.set(value);
        let fallbackTargetUrl = targetUrl || globalThis.location?.href || globalThis.location?.pathname || '/';
        if (normalizedValue === this.newValue) {
            fallbackTargetUrl = this.stripLegacySuffixIfKnown(fallbackTargetUrl);
        }
        globalThis.location?.assign(fallbackTargetUrl);
        return normalizedValue;
    },

    // Recarregar a mesma URL depois de aceitar o novo visual devolveria a tela legada: as rotas
    // -legacy apontam direto para o aspx, sem passar pelo controller que escolhe a versão (ver
    // TodayServicesController e TodayTasksController). Quando a página atual tem equivalente novo, a
    // troca navega para ele; nas demais devolve vazio e o persistAndReload recarrega a URL atual.
    newDesignUrl() {
        const location = globalThis.location;
        if (!location) return '';

        // Remove barras finais sem regex: /\/+$/ é quadrático em sequências longas de "/"
        // que não terminam a string (apontado pelo Sonar como risco de ReDoS).
        let path = location.pathname ?? '';
        let end = path.length;
        while (end > 0 && path[end - 1] === '/') end--;
        path = path.slice(0, end);

        // Ancorado no fim do segmento: sem isso /manage/integrations-legacy-v2 viraria
        // /manage/integrations-v2, que não existe. O resto do caminho (diretório virtual antes,
        // pathInfo depois) é preservado.
        const suffix = /-legacy(?=\/|$)/i.exec(path);
        if (!suffix) return '';

        // Dentro de um app de processo (app-{código}/...) as telas novas não têm rota: trocar a URL
        // daria 404 ou jogaria a pessoa pra fora do app. Lá a troca só recarrega a página.
        if (/(^|\/)app-[^/]+\//i.test(path)) return '';

        const suffixEnd = suffix.index + suffix[0].length;
        const legacyPath = path.slice(0, suffixEnd).toLowerCase();
        if (this.legacyPathsWithoutNewVersion.some(item => legacyPath.endsWith('/' + item))) return '';

        return path.slice(0, suffix.index) + path.slice(suffixEnd) + (location.search ?? '') + (location.hash ?? '');
    },

    enableNewAndReload(targetUrl) {
        return this.persistAndReload(globalThis.appvars?.services?.users?.preferencesnewlayout, targetUrl || this.newDesignUrl());
    },

    enableLegacyAndReload(targetUrl) {
        this.clearEmbedSessionFlag();
        return this.persistAndReload(globalThis.appvars?.services?.users?.preferenceslegacylayout, targetUrl);
    }
};

DockLayoutPreference.init();
globalThis.DockLayoutPreference = DockLayoutPreference;
}
